import React from 'react'
import { AppointmentQueryParams, useAppointmentQueryParams } from './useAppointmentQueryParams'
import { useResolvedAppointmentContext } from './useResolvedAppointmentContext'
import { useAppointmentLoader } from './useAppointmentLoader'
import { useAppointmentFields } from './useAppointmentFields'
import { useLoader } from './useLoader'
import { appointmentsService, IAppointmentChanges } from '../services/AppointmentsService'

const initialChanges = (): IAppointmentChanges => ({
  notes: null,
  status: null,
  extraFields: {}
})

const getExtraFieldsKey = (extraFields: any[]) => {
  return extraFields
    .map(f => f.fieldID)
    .filter(Boolean)
    .sort()
    .join(',')
}

export const useAppointmentScreenController = () => {
  const params = useAppointmentQueryParams()
  const { businessId, resourceId } = useResolvedAppointmentContext(params)
  const { appointment, loading, reload } = useAppointmentLoader(params.id, businessId)
  const { fieldGroups, fieldPresets, fieldsList } = useAppointmentFields(businessId)

  const extraFields = appointment?.extraFields || []
  const [changes, setChanges] = React.useState<IAppointmentChanges>(initialChanges())
  const clearChanges = () => setChanges(initialChanges())
  const extraFieldKey = React.useMemo(() => getExtraFieldsKey(extraFields), [extraFields])

  React.useEffect(() => {
    clearChanges()
  }, [extraFieldKey])

  const [applying, applyChanges] = useLoader(async (payload: IAppointmentChanges) => {
    if (!params.id || !businessId || !resourceId) return
    await appointmentsService.updateAppointment({
      id: params.id,
      businessId,
      resourceId,
      changes: payload
    })
  })
  const [canceling, cancelAppointment] = useLoader(async () => {
    if (!params.id || !businessId) return
    await appointmentsService.cancelAppointment({ id: params.id, businessId })
    await reload()
  })

  const hasChanges = changes.notes != null || Object.keys(changes.extraFields).length > 0

  return {
    data: {
      appointment,
      extraFields,
      fieldGroups,
      fieldPresets,
      fieldsList,
    },
    form: {
      changes,
      setChanges,
      clearChanges,
      hasChanges,
    },
    actions: {
      applyChanges,
      cancelAppointment,
    },
    status: {
      loading,
      applying,
      canceling,
    },
    context: {
      businessId,
      resourceId,
    }
  }
}
